import { httpClient } from 'providers/clients/httpClient';
import {
  PostCoreUploadParams,
  PostCoreUploadResult,
  GetCoreUploadIdParams,
  GetCoreUploadIdResult,
  DeleteCoreUploadIdParams,
  DeleteCoreUploadIdResult,
  PostEditorialAssetBinaryParams,
  PostEditorialAssetBinaryResult,
} from 'utility/types/dataProvider';

// -----------------------------------------------
// --------------  BINARY PROVIDER ---------------
// -----------------------------------------------

export const binaryProvider = {
  // post /core/upload/
  postCoreUpload: async (params: PostCoreUploadParams): Promise<PostCoreUploadResult> => {
    try {
      const path = '/core/upload/';

      // create a FormData object to handle file uploads
      const formData = new FormData();
      if (params?.file) {
        formData.append('file', params.file);
      }

      // extract axiosConfig from params for spread
      // if exists abort signal, onUploadProgress, etc.
      const axiosReqConfig = params?.axiosReqConfig || {};

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: formData,
        ...axiosReqConfig,
      });

      const result: PostCoreUploadResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[postCoreUpload] Error:', error);
      throw error;
    }
  },

  // get /core/upload/id/
  getCoreUploadById: async (
    params: GetCoreUploadIdParams,
  ): Promise<GetCoreUploadIdResult> => {
    try {
      if (!params?.id) {
        throw new Error('Missing required parameter: id');
      }

      const path = `/core/upload/${params?.id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetCoreUploadIdResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getCoreUploadId] Error:', error);
      throw error;
    }
  },

  // delete /core/upload/id/ - discard without consuming
  deleteCoreUploadById: async (
    params: DeleteCoreUploadIdParams,
  ): Promise<DeleteCoreUploadIdResult> => {
    try {
      if (!params?.id) {
        throw new Error('Missing required parameter: id');
      }

      const path = `/core/upload/${params?.id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      // 204 No Content on Success
      const result: DeleteCoreUploadIdResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteCoreUploadId] Error:', error);
      throw error;
    }
  },

  // above api is often used in conjuction with
  // the editorial/asset/binary calls:

  // post /editorial/asset/binary/
  // creates an asset from a binary upload (aka batch upload)
  postEditorialAssetBinary: async (
    params: PostEditorialAssetBinaryParams,
  ): Promise<PostEditorialAssetBinaryResult> => {
    try {
      const path = '/editorial/asset/binary/';

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(params),
      });

      const result: PostEditorialAssetBinaryResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[postEditorialAssetBinary] Error:', error);
      throw error;
    }
  },
};
